import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';

import { Route, Switch } from '@bingads-webui-campaign-react/router';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withScope } from '@bingads-webui-react/scope-service';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withFooter } from '@bingads-webui-campaign-react/content-footer-hoc';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import {
  withAccountsSiteMap,
  withCampaignSiteMap,
} from '@bingads-webui-campaign-react/with-site-map';
import {
  withLayout,
  withFullScreenLayout,
  withFullScreenWithSummaryBar,
  withDefaultLayout,
  withMenuOnlyLayout,
  withoutMenuLayout,
  withSingleTabLayout,
  layoutConfigs,
  withLayoutNavRefresh,
  withFullScreenLayoutNavRefresh,
  withSummaryBarAndTabNoDateLayoutNavRefresh,
  withSummaryBarAndTabAndSubTabLayoutNavRefresh,
  withFullScreenWithSummaryBarNavRefresh,
  withFullScreenWithSummaryBarAndTabLayoutNavRefresh,
  withFullScreenWithSummaryBarNavRefreshNoMenu,
  withLayoutNavRefreshWithTab,
  withLayoutNavRefreshNoMenu,
} from '@bingads-webui-react/app-layout-container';
import { AppContentContainer } from '@bingads-webui-react/app-content-container';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { PageReady } from '@bingads-webui/page-ready';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import { DefaultGlobalErrorView, withChunkErrorActionGlobalErrorView as withGlobalErrorView } from '@bingads-webui-campaign-react/global-error-options';
import { withOpportunityId } from '@bingads-webui-campaign-react/with-opportunity-id';
import { AccountsPageNavigation } from './accounts-page/accounts-page';
import { routes as r } from './dynamic-routes';
import { lazyLoad, isLandingPage, withContexts, withProps } from './utils';
import { withPagePerfMarker, withOverviewPerfMarker } from '../contexts/with-page-perf-marker';
import { addBadgeForNewFeatures } from './new-feature-badge';
import { withBingMap } from '../contexts/with-bing-map';
import { withLegacyContext, withCampaignLegacyContext } from '../legacy-dep-bootstrap/legacy-context';
import { adExtensionsLoader, overviewPageLoader, campaignsPageLoader, performancePageLoader } from './feature-loaders';
import { withPageName } from './preexecute/with-page-name';
import { withPreexecute } from './preexecute/with-preexecute';
import { withAppPerfMarker } from '../contexts/app-perfmarker-context';
import { withDFMReady } from '../data-fetch/with-dfm';
import { withCcJsDataContextProvider } from '../contexts/with-cc-js-data-context-provider';
import { CampaignOnboardingShell } from './campaign-onboarding-shell';
import { WebsiteExclusionListsCreateShell, WebsiteExclusionListsEditShell, WebsiteExclusionListsViewListsShell, WebsiteExclusionListsViewExclusionsShell } from './website-exclusion-lists-shell';
import { withClipchampPlayer } from './withClipchampPlayer';

// Using perf marker factory is the best option here during module import and initial execution. For other places we should continue to use appPerfMarker.
const appRoutesInitPerfMarker = PerfMarkerFactory.createMarker('ShellPage_WebUI.App_Routes_Init');
appRoutesInitPerfMarker.willInit();

const withContextsNoFallback = _.compose(withI18nConsumer, withPermissions, withPreference, withScope);
const reporting = withLegacyContext(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.Reports, c => withContextsNoFallback(withServerContext(c))), 'Route_Reports_VNext')));
const ReportsV4 = withLegacyContext(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.ReportsV4, c => withContextsNoFallback(withServerContext(c))), 'Route_Reports_V4')));
const Fallback = () => (<DefaultGlobalErrorView shouldShowRefreshLink={false} />);
const pageReadyTimeout = 15000;

let isPageReadyResolved = false;

let firstRoute = '';

// We need to cache the shell of Product Group Page in this instance. Or every time the router changes, the shell will be rebuilt.
// todo: when we change all pages to use router render method, merge all page with dynamic-route
const BidStrategiesShell = withLegacyContext(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.BidStrategiesShell, c => withContexts(withServerContext(c))), 'Route_BidStrategies')));
const StyledChangeHistory = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledChangeHistory, c => withContexts(withServerContext(c))), 'Route_ChangeHistory_VNext'))));
const HotelGroupsShell = withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.HotelGroupsShell, c => withContexts(withServerContext(c))), 'Route_HotelGroups'))));
const PropertyGroupsShell = withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.PropertyGroupsShell, c => withContexts(withServerContext(c))), 'Route_PropertyGroups'))));
const ProductGroupsShell = withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.ProductGroupsShell, c => withContexts(withServerContext(c))), 'Route_ProductGroups_VNext'))));
const PerformanceTargetsShell = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.PerformanceTargetsShell, c => withContexts(withServerContext(c))), 'Route_PerformanceTargets_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutNav, ...layoutConfigs.withoutDate }));
const StyledCampaigns = withCcJsDataContextProvider(withDFMReady(withPageName(withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(campaignsPageLoader, 'Route_Campaigns_VNext')))), 'campaigns')));
const StyledCampaignsPreexecution = withCcJsDataContextProvider(withPageName(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(campaignsPageLoader, 'Page_Preexecute_campaigns'))), 'campaigns'));
const StyledCampaignCreationHotel = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.HotelCampaignCreation, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_HotelCampaignCreation'))));
const StyledCampaignCreationSearch = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationSearch, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AdvancedCampaignCreationWizard_VNext'))));
const StyledCampaignCreationSearchV2 = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationSearchV2, c => withBingMap(withContexts(withServerContext(c)))), 'Route_SearchCampaignCreationWizardV2_VNext'))));
const StyledCampaignCreationAudience = withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationAudience, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AudienceCampaignCreationWizard_VNext')));
const StyledCampaignCreationAudienceSimplified = withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationAudienceSimplified, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AudienceCampaignCreationSimplifiedWizard_VNext')));
const StyledCampaignCreationAudienceVideo = withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationAudienceVideo, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AudienceCampaignCreationWizard_VNext')));
const StyledCampaignCreationSearchShopping = withCcJsDataContextProvider(withDFMReady(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationSearchShopping, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AdvancedCampaignShoppingCreationWizard_VNext')))));
const StyledCampaignCreationSearchShoppingWizard = withCcJsDataContextProvider(withDFMReady(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationSearchShoppingWizard, c => withBingMap(withContexts(withServerContext(c)))), 'Route_AdvancedCampaignNewShoppingCreationWizard_VNext')))));
const StyledCampaignCreationAudienceShoppingWizard = withCcJsDataContextProvider(withDFMReady(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationAudienceShoppingWizard, c => withBingMap(withContexts(withServerContext(c)))), 'Route_AdvancedCampaignNewShoppingCreationAudienceWizard_VNext')))));
const StyledSponsoredCampaignCreation = withDFMReady(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.SponsoredCampaignCreationPage, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_SpaSpbCampaignCreation_VNext'))));
const StyledCampaignCreationMSStoreAds = withCcJsDataContextProvider(withDFMReady(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationMSStoreAds, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_MSStoreAdsCampaignCreationWizard_VNext')))));
const StyledCampaignCreationAudienceShopping = withDFMReady(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationAudienceShopping, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AudienceCampaignShoppingCreationWizard_VNext'))));
const PerfMaxCampaignCreationPage = withCcJsDataContextProvider(withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.PerfMaxCampaignCreationPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_PerfMaxCampaignCreationPage_VNext')))));
const LinkedInCampaignCreationPage = withFullScreenWithSummaryBar(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.LinkedInCampaignCreationPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_LinkedInCampaignCreationPage_VNext'))));
const LinkedInAdEditorPage = withFullScreenWithSummaryBar(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.LinkedInAdEditorPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_LinkedInAdEditorPage_VNext'))));
// const LinkedInAdEditorPage = withFullScreenWithSummaryBar(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.LinkedInAdEditorPage, c => withContexts(withServerContext(c))), 'Route_LinkedInAdEditorPage_VNext'))));
const PerfMaxNorthStarCampaignCreationPage = withCcJsDataContextProvider(withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.PerfMaxNorthStarWizard, c => withBingMap(withContexts(withServerContext(c)))), 'Route_PerfMaxCampaignCreationPage_NorthStar')))));
const StyledXandrVideoCampaignCreationPage = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledXandrVideoCampaignCreationPage, c => withBingMap(withContexts(withServerContext(c))))), 'Route_XandrVideoCampaignCreationPage_VNext')));
const StyledXandrDisplayAdsCampaignCreationPage = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledXandrDisplayAdsCampaignCreationPage, c => withBingMap(withContexts(withServerContext(c))))), 'Route_XandrDisplayAdsCampaignCreationPage_VNext')));
const StyledDisplayPlusCampaignsPage = withClipchampPlayer(withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledDisplayPlusCampaignsPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_DisplayPlusCampaigns')))));
const StyledDisplayPlusCampaignsDisplayAdsPage = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledDisplayPlusCampaignsPage, c => withBingMap(withContexts(withServerContext(withProps(c, { adsType: 'Display' }))))), 'Route_DisplayPlusCampaigns'))));
const StyledAppAdsCampaignsPage = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledAppAdsCampaignsPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_AppAdsCampaigns'))));
const StyledAppAdsAssetGroupCreation = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledAppAdsAssetGroupCreation, c => withBingMap(withContexts(withServerContext(c)))), 'Route_AppAdsAssetGroupCreatio'))));
const AdGroupCreationWithCampaign = withClipchampPlayer(withCampaignLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.AdGroupCreationWithCampaign, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AdGroup_Creation_VNext')))));
const StyledAdGroups = withDFMReady(withPageName(withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAdGroups, c => withContexts(withServerContext(c))), 'Route_AdGroup_VNext')))), 'adgroups'));
const StyledAdGroupsPreexecution = withPageName(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAdGroups, c => withContexts(withServerContext(c))), 'Page_Preexecute_adgroups'))), 'adgroups');
const StyledAds = withClipchampPlayer(withOpportunityId(withDFMReady(withPageName(withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAds, c => withContexts(withServerContext(c))), 'Route_Ads_VNext')))), 'ads'))));
const StyledAdsPreexecution = withPageName(withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAds, c => withContexts(withServerContext(c))), 'Page_Preexecute_ads')))), 'ads');
const StyledPositiveKeywords = withOpportunityId(withDFMReady(withPageName(withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPositiveKeywords, c => withContexts(withServerContext(c))), 'Route_Keywords_VNext')))), 'keywords')));
const StyledPositiveKeywordsPreexecution = withPageName(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPositiveKeywords, c => withContexts(withServerContext(c))), 'Page_Preexecute_keywords'))), 'keywords');
const StyledNegativeKeywords = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledNegativeKeywords, c => withContexts(withServerContext(c))), 'Route_Negative_Keywords_VNext'))));
const StyledSearchTerms = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledSearchterm, c => withContexts(withServerContext(c))), 'Route_Searchterms_VNext'))));
const StyledBulkEdits = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledBulkEdits, c => withContexts(withServerContext(c))), 'Route_BulkEdits_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledBulkEditsDetail = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledBulkEditsDetail, c => withContexts(withServerContext(c))), 'Route_BulkEditsDetail_VNext')), {
  ...layoutConfigs.fullScreenWithSummaryBar,
  ...layoutConfigs.withoutDate,
}));
const MCMCampaignControl = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.MCMCampaignControl, c => withContexts(withServerContext(c))), 'Route_MCMCampaignControl_VNext')), layoutConfigs.withCollapsedMenuOnly));

const StyledAudiencePlanner = withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceNetworkPlanner, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_AudiencePlanner_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledCampaignCreationSmartShoppingPage = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCampaignCreationSmartShopping, c => withFooter(withBingMap(withContexts(withServerContext(c))))), 'Route_CampaignCreationSmartShopping_VNext')))));
const recommendations = lazyLoad(r.RecommendationsShell, c => withContexts(withServerContext(c)));
const recommendationsWithPerfMarker = withGlobalErrorView(withPagePerfMarker(recommendations, 'Route_Recommendations_VNext'));
const mccRecommendationsWithPerfMarker = withGlobalErrorView(withPagePerfMarker(recommendations, 'Route_MCC_Recommendations_VNext'));
const StyledRecommendations = withCcJsDataContextProvider(withLayoutNavRefresh(recommendationsWithPerfMarker, layoutConfigs.withoutDate));
const StyledMCCRecommendations = withCcJsDataContextProvider(withLayoutNavRefresh(mccRecommendationsWithPerfMarker, {
  ...layoutConfigs.accountSummary,
  ...layoutConfigs.withHistoryButton,
}));
const StyledRecommendationsWithHistoryButton = withCcJsDataContextProvider(withDFMReady(withLayoutNavRefresh(recommendationsWithPerfMarker, layoutConfigs.withHistoryButton)));
const StyledRecommendationsHistoryPage = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(recommendationsWithPerfMarker));
const StyledMCCRecommendationsHistoryPage = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(mccRecommendationsWithPerfMarker));
const StyledRecommendationRSAAsset = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(recommendations));
const StyledRecommendationImageAsset = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(recommendations));
const StyledRecommendationDemo = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(recommendations));
const StyledRecommendationAutoApplySettinsPage = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(recommendationsWithPerfMarker));
const StyledRecommendationAutoApplyBulkUploadPage = withCcJsDataContextProvider(withFullScreenLayoutNavRefresh(mccRecommendationsWithPerfMarker));
const StyledFeeds = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledFeeds, c => withContextsNoFallback(withServerContext(c))), 'Route_Feeds_VNext')), layoutConfigs.withoutNav));
const StyledImportCenter = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledImportCenter, c => withContexts(withServerContext(c))), 'Route_ImportCenter_VNext')), { ...layoutConfigs.withoutMenu, ...layoutConfigs.withoutDate })));
const StyledAuctionInsightPage = withCcJsDataContextProvider(withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCompetitionAuctionInsight, c => withContexts(withServerContext(c))), 'Route_CompetitionAuctionInsight_VNext')))));
const StyledAudienceAssociationPage = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceAssociationPage, c => withContexts(withServerContext(c))), 'Route_Audiences_VNext'))));
const StyledAudiencesLibrary = withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudiencesLibrary, c => withContexts(withServerContext(c))), 'Route_AudienceSharedLibrary_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledAudiencesLibraryV2 = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudiencesLibrary, c => withContexts(withServerContext(c))), 'Route_AudienceSharedLibrary_VNext'))));
const StyledAudienceSignalManagement = withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceSignalManagement, c => withContexts(withServerContext(withPermissions(c))), 'Route_AudienceSignalManagement_VNext'))));
const StyledAudienceCustomSegments = withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceCustomSegmentsPage, c => withContexts(withServerContext(withPermissions(c))), 'Route_AudienceCustomSegments_VNext'))));
const StyledAudienceAssociationCreation = withLegacyContext(withFullScreenLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceAssociationCreation, c => withFooter(withContexts(withServerContext(c)))), 'Route_AudienceAssociationCreation_VNext'))));
const StyledAudienceCreation = withLegacyContext(withFullScreenLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceCreation, c => withFooter(withContexts(withServerContext(c)))), 'Route_AudienceCreation_VNext'))));
const StyledAudienceEdit = withFullScreenLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAudienceEdit, c => withFooter(withContexts(withServerContext(c)))), 'Route_AudienceEdit_VNext')));
const StyledConversionGoalsAutoCreate = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionGoalsAutoCreate, c => withFooter(withContexts(withServerContext(c)))), 'Route_ConversionGoalsAutoCreate_VNext')))));
const StyledConversionGoalsCreate = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionGoalsCreate, c => withFooter(withContexts(withServerContext(c)))), 'Route_ConversionGoalsCreate_VNext')))));
const StyledConversionGoalsEdit = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionGoalsEdit, c => withFooter(withContexts(withServerContext(c)))), 'Route_ConversionGoalsEdit_VNext')))));
const StyledConversionValueRulesCreate = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionValueRulesCreate, c => withFooter(withContexts(withServerContext(c)))), 'Route_ConversionValueRulesCreate_VNext')))));
const StyledConversionValueRulesEdit = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionValueRulesCreate, c => withFooter(withContexts(withServerContext(c)))), 'Route_ConversionValueRulesEdit_VNext')))));
const StyledOverviewPage = withPageName(withLegacyContext(withDefaultLayout(withGlobalErrorView(withOverviewPerfMarker(overviewPageLoader)))), 'overview');
const StyledUetTags = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTags, c => withContexts(withServerContext(c))), 'Route_UetTags_VNext')), layoutConfigs.fullScreenWithSummaryBar)));
const StyledUetAllTagsDashboard = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTags, c => withContexts(withServerContext(c))), 'Route_UetTags_Dashboard_VNext')), {
  ...layoutConfigs.accountSummaryWithoutDate,
  tab: { mode: 'hidden' },
})));
const StyledUetTagsDashboard = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTags, c => withContexts(withServerContext(c))), 'Route_UetTags_Dashboard_VNext')), {
  ...layoutConfigs.accountSummaryWithoutDate,
  ...layoutConfigs.withDisabledDate,
})));
const StyledUetDashboard = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTags, c => withContexts(withServerContext(c))), 'Route_UetTags_Dashboard_VNext')), { ...layoutConfigs.accountSummaryWithoutDate, ...layoutConfigs.dateWithoutCompare })));
const StyledUetWebInsights = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetWebInsights, c => withContexts(withServerContext(c))), 'Route_StyledUetWebInsights_VNext')), { ...layoutConfigs.accountSummaryWithoutDate, ...layoutConfigs.dateWithoutCompare }));
const StyledUetTestPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTestPage, c => withContexts(withServerContext(c))), 'Route_StyledUetTestPage_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledUetTagsCreate = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTagsCreate, c => withFooter(withContexts(withServerContext(c))), 'Route_UetTagsCreate_VNext'))))));
const StyledUetTagsEdit = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUetTagsEdit, c => withFooter(withContexts(withServerContext(c))), 'Route_UetTagsEdit_VNext'))))));
const UniversalPixelUetTagCreate = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.UniversalPixelUetTagCreate, c => withFooter(withContexts(withServerContext(c))), 'Route_UetUpTagsCreate_VNext'))))));
const UniversalPixelUetTagEdit = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.UniversalPixelUetTagEdit, c => withFooter(withContexts(withServerContext(c))), 'Route_UetUpTagsEdit_VNext'))))));
const StyledOfflineConversions = withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledOfflineConversions, c => withFooter(withContexts(withServerContext(c)))), 'Route_OfflineConversions_VNext')), { ...layoutConfigs.fullScreenWithSummaryBar }));
const StyledModeledConversions = withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledModeledConversions, c => withFooter(withContexts(withServerContext(c)))), 'Route_ModeledConversions_VNext')), { ...layoutConfigs.fullScreenWithSummaryBar }));
const StyledConversionGoals = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionGoals, c => withContexts(withServerContext(c))), 'Route_ConversionGoals_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.dateWithoutCompare })));
const StyledConversionValueRules = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledConversionValueRules, c => withContexts(withServerContext(c))), 'Route_ConversionValueRules_VNext')))); const StyledSharedBudgetEdit = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledSharedBudgetEdit, c => withContexts(withServerContext(c))), 'Route_SharedBudgetEdit_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.dateWithoutCompare }));
const StyledCustomerAcquisition = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCustomerAcquisition, c => withContexts(withServerContext(c))), 'Route_CustomerAcquisition_VNext'))));
const StyledHotelCenter = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledHotelCenter, c => withContexts(withServerContext(c))), 'Route_HotelCenter_VNext')), { ...layoutConfigs.accountSummary, ...layoutConfigs.dateWithoutCompare }));
const StyledHotelCenterV2 = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledHotelCenterV2, c => withFooter(withContexts(withServerContext(c)))), 'Route_HotelCenterV2_VNext')), { ...layoutConfigs.fullScreenWithSummaryBar }));
const StyledSharedBudgetSummary = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledSharedBudgetSummary, c => withContexts(withServerContext(c))), 'Route_SharedBudgetSummary_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.dateWithoutCompare }));
const StyledSeasonalityAdjustment = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledSeasonalityAdjustment, c => withContexts(withServerContext(c))), 'Route_SeasonalityAdjustment_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate }));
const StyledPortfolioBidStrategy = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPortfolioBidStrategy, c => withContexts(withServerContext(c))), 'Route_PortfolioBidStrategy_VNext')), {
  ...layoutConfigs.fullScreenWithSummaryBarAndTab,
  tab: {}, // Do not specify mode: 'collapsed' or 'expanded', let date picker make the decision based on whether compared date range is enabled.
}));
const StyledExtensions = withDFMReady(withLegacyContext(withPermissions(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(adExtensionsLoader, 'Route_AdExtensions_VNext'))))));
const StyledExperiments = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledExperiments, c => withContexts(withServerContext(c))), 'Route_Experiments_VNext')), layoutConfigs.withoutDate));
const StyledProductFeeds = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledProductFeedPage,
  c => withContexts(withServerContext(c))
), 'Route_ProductFeed_VNext'))));
const StyledLinkedStorePage = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledLinkedStorePage,
  c => withContexts(withServerContext(c))
), 'Route_LinkedStore_VNext'))));
const StyledBusinessLocationFeed = withCcJsDataContextProvider(withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledGmbImport, c => withContexts(withServerContext(c))), 'Route_GMBImport_VNext')))));
const StyledHotelsPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledHotels, c => withContexts(withServerContext(c))), 'Route_Hotels'))));
const StyledPropertiesPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledProperties, c => withContexts(withServerContext(c))), 'Route_Properties'))));
const StyledProductsPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledProducts,
  c => withContexts(withServerContext(c))
), 'Route_Products_VNext')), layoutConfigs.withoutDate));
const StyledFeedsSummaryPage = withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledFeedSummaryPage,
  c => withContexts(withServerContext(c))
), 'Route_FeedSummary_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate });
const StyledMSStoreAdsSellerIdsPage = withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledMSStoreAdsSellerIdsPage,
  c => withContexts(withServerContext(c))
), 'Route_MSStoreAdsSellerIds_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate });
const StyledDemographicsPage = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.DemographicsShell, c => withContexts(withServerContext(c))), 'Route_Demographics_VNext'))));
const StyledTargetedLocationsPage = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledTargetedLocations, c => withBingMap(withContexts(withServerContext(c)))), 'Route_Locations_VNext'))));
const StyledExcludedLocationsPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledExcludedLocations,
  c => withBingMap(withContexts(withServerContext(c)))
), 'Route_ExcludedLocations_VNext')), layoutConfigs.withoutDate));
const StyledAdSchedulePage = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAdSchedule, c => withContexts(withServerContext(c))), 'Route_AdSchedule_VNext'))));
const StyledDevicesPage = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledDevices, c => withContexts(withServerContext(c))), 'Route_Devices_VNext'))));
const StyledBrandPage = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.StyledBrandPage,
  c => withContexts(withServerContext(c))
), 'Route_Brand_VNext'))));
const StyledCompetitionRecommendation = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(lazyLoad(r.StyledCompetitionRecommendations, c => withBingMap(withContexts(withServerContext(c))))), layoutConfigs.withoutDate)));
const StyledDynamicAdTarget = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledDynamicAdTarget, c => withContexts(withServerContext(c))), 'Route_DynamicAdTarget_VNext'))));
const DynamicExclusionShell = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.DynamicExclusionShell, c => withContexts(withServerContext(c))), 'Route_DynamicExclusion_VNext'))));
const StyledDynamicSearchTerm = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledDynamicSearchTerm, c => withContexts(withServerContext(c))), 'Route_DynamicSearchTerm_VNext'))));
const StyledGoogleImport = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledGoogleImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_GoogleImport_VNext'))));
const StyledMultiAccountGoogleImport = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledMultiAccountGoogleImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_MultiAccountGoogleImport_VNext'))));
const StyledFileImport = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledFileImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_FileImport_VNext'))));
const StyledImportSchedule = withCcJsDataContextProvider(withLegacyContext(withFullScreenWithSummaryBarNavRefreshNoMenu(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledImportSchedule, c => withFooter(withContexts(withServerContext(c)))), 'Route_ImportSchedule_VNext')))));
const StyledImportCredentialId = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledImportCredentialId, c => withFooter(withContexts(withServerContext(c)))), 'Route_ImportCredentialId_VNext', false))));
const StyledAccountSettingsPage = withCcJsDataContextProvider(withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAccountSettingsPage, c => withContexts(withServerContext(c))), 'Route_AccountSettings_VNext')), layoutConfigs.withoutDate)));
const MerchantCenterShell = withCcJsDataContextProvider(withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.MerchantCenterShell, c => withContexts(withServerContext(c))), 'Route_MerchantCenter_VNext')), layoutConfigs.withoutDate)));
const ReportingFullScreen = withFullScreenWithSummaryBarNavRefresh(reporting);
const ReportingWithNav = withMenuOnlyLayout(reporting);
// const ReportsV4FullScreen = withFullScreenWithSummaryBar(ReportsV4);
const ReportsV4WithNav = withCcJsDataContextProvider(withDFMReady(withLayoutNavRefreshNoMenu(ReportsV4, {
  ...layoutConfigs.withoutMenu,
  status: { mode: 'hidden' },
  tab: { mode: 'hidden' },
  subTab: { mode: 'hidden' },
})));
const StyledCustomReportingPage = withCcJsDataContextProvider(withLayoutNavRefreshNoMenu(withGlobalErrorView(withInstrumentation(withDFMReady(withPagePerfMarker(lazyLoad(r.StyledCustomReportingPage, c => withContexts(withServerContext(c))), 'Route_New_Reporting_UI')))), {
  ...layoutConfigs.withoutMenu,
  status: { mode: 'hidden' },
  tab: { mode: 'hidden' },
  subTab: { mode: 'hidden' },
}));
const StyledBrandSafetyPage = withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.BrandSafetyPage, c => withContexts(withServerContext(c))), 'Route_BrandSafety_VNext')));
const StyledBrandListsPage = withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledBrandListsPage, c => withFooter(withContexts(withServerContext(c)))), 'Route_BrandLists_VNext')));
const StyledBrandListsDetailsPage = withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledBrandListsDetailsPage, c => withFooter(withContexts(withServerContext(c)))), 'Route_BrandListDetails_VNext')));

const StyledAccountLevelImportTab = withCcJsDataContextProvider(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAccountLevelImportTab, c => withContexts(withServerContext(c))), 'Route_AccountsSummaryImport_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledDimensionsShell = withLegacyContext(withSingleTabLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.DimensionsShell, c => withContexts(withServerContext(c))), 'Route_Dimensions_VNext'))));
const StyledDimensionsCampaignShell = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.CampaignDimensionShell, c => withContexts(withServerContext(c))), 'Route_AdSchedule_Hour_VNext'))));
const styledDimensionsCampaignShellWithSubTab = withLegacyContext(withSummaryBarAndTabAndSubTabLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.CampaignDimensionShell, c => withContexts(withServerContext(c))), 'Route_Dimensions_WithSubTab_VNext'))));
const NegativeKeywordsLists = withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.NegativeKeywordsLists, c => withContexts(withServerContext(c))), 'Route_NegativeKeywordsLists_VNext')), layoutConfigs.withoutDate);
const NegativeKeywordListsLibrary = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(
  r.NegativeKeywordListsLibrary,
  c => withContexts(withServerContext(c))
), 'Route_NegativeKeywordListsLibrary_VNext')), { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate }));
const StyledAccountsOverview = withPageName(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withOverviewPerfMarker(overviewPageLoader, 'Route_AccountsOverview_VNext')), layoutConfigs.accountSummary)), 'accounts-overview');
const StyledAccountsPerformance = withCcJsDataContextProvider(withPageName(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(performancePageLoader, 'Route_AccountsPerformance_VNext')), layoutConfigs.accountSummary)), 'accounts-summary'));
const StyledAccountsPerformancePreexecution = withCcJsDataContextProvider(withPageName(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(performancePageLoader, 'Page_Preexecute_accounts-summary')), layoutConfigs.accountSummary), 'accounts-summary'));
const StyledAccountsManagement = withCcJsDataContextProvider(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledManagement, c => withContexts(withServerContext(c))), 'Route_AccountsManagement_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledAccountsNotifications = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledNotifications, c => withContexts(withServerContext(c))), 'Route_AccountsNotifications_VNext')), layoutConfigs.accountSummaryWithoutDate)));
const StyledAccountsRequests = withCcJsDataContextProvider(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledRequests, c => withContexts(withServerContext(c))), 'Route_AccountsRequests_VNext')), layoutConfigs.accountSummaryWithoutDate)));
const StyledAccountsAuctionInsight = withPageName(withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCompetitionAuctionInsight, c => withContexts(withServerContext(c))), 'Route_AccountsAuctionInsight_VNext')), layoutConfigs.accountSummary)), 'accounts-auction');
const StyledAccountsCreate = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.AccountCreation, c => withFooter(withContexts(withServerContext(c)))), 'Route_AccountsCreate_VNext'))));
const StyledBulkTransferAccounts = withCcJsDataContextProvider(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.AccountsTransfer, c => withContexts(withServerContext(c))), 'Route_BulkTransferAccounts_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledPendingTransferRequest = withCcJsDataContextProvider(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.PendingTransferRequest, c => withContexts(withServerContext(c))), 'Route_PendingTransferRequest_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledSendLinkRequest = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.AccountLinking, c => withFooter(withContexts(withServerContext(c)))), 'Route_SendLinkRequest_VNext'))));
const StyledResendLinkRequest = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.AccountLinking, c => withFooter(withContexts(withServerContext(c)))), 'Route_ResendLinkRequest_VNext'))));
const StyledBulkUnlinkAccounts = withCcJsDataContextProvider(withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.AccountsUnlinking, c => withFooter(withContexts(withServerContext(c)))), 'Route_BulkUnlinkAccounts_VNext'))));
const StyledAutomatedRuleOperation = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAutomatedRuleOperation, c => withContexts(withServerContext(c))), 'Route_AutomatedRules_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledAutomatedRuleHistory = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAutomatedRuleHistory, c => withContexts(withServerContext(c))), 'Route_AutomatedRules_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledAutomatedRuleDetail = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAutomatedRuleDetail, c => withContexts(withServerContext(c))), 'Route_AutomatedRules_VNext')), {
  ...layoutConfigs.fullScreenWithSummaryBar,
  ...layoutConfigs.withoutDate,
}));
const StyledSettingsForm = withCcJsDataContextProvider(withLegacyContext(withLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledSettingsForm, c => withBingMap(withContexts(withServerContext(c)))), 'Route_Settings_Form_VNext'))), layoutConfigs.withoutDate)));
const StyledNegativeKeywordsListsDetailsShell = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.NegativeKeywordListsDetailsPage, c => withContexts(withServerContext(c))), 'Route_NegativeKeywordsListsDetails_Grid_VNext')), layoutConfigs.withoutDate));
const StyledLabels = withLegacyContext(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledLabels, c => withContexts(withServerContext(c))), 'Route_Labels_VNext'))));
const StyledCombinations = withLegacyContext(withFullScreenWithSummaryBarAndTabLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledCombinations, c => withContexts(withServerContext(c))), 'Route_Combinations_VNext'))));
const StyledAssets = withFullScreenWithSummaryBarAndTabLayoutNavRefresh(withLegacyContext(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAssets, c => withContexts(withServerContext(c))), 'Route_Assets_VNext'))));
const StyledAssetsReport = withLegacyContext(withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAssetsReport, c => withContexts(withServerContext(c))), 'Route_RSA_Assets_VNext'))));
const StyledScripts = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledScripts, c => withContexts(withServerContext(c))), 'Route_ScriptsPage_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledScriptsEdit = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledScriptsEdit, c => withContexts(withServerContext(c))), 'Route_ScriptsEditPage_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledScriptsHistory = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledScriptsHistory, c => withContexts(withServerContext(c))), 'Route_ScriptsHistoryPage_VNext')), layoutConfigs.accountSummaryWithoutDate));
const StyledScriptsLogs = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledScriptsLogs, c => withContexts(withServerContext(c))), 'Route_ScriptsLogsPage_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const BulkUploadsShell = withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.BulkUploadsShell, c => withContexts(withServerContext(c))), 'Route_BulkUploads_VNext')), layoutConfigs.accountSummaryWithoutDate);
const StyledSmartGoogleImportShell = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledSmartGoogleImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_Smart_Google_Import_VNext'))));
const StyledAmazonImport = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAmazonImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_Amazon_Import_VNext'))));
const StyledSmartFacebookImportShell = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledSmartFacebookImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_Smart_Facebook_Import_VNext'))));
const StyledPinterestImport = withCcJsDataContextProvider(withFullScreenWithSummaryBar(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPinterestImport, c => withFooter(withContexts(withServerContext(c)))), 'Route_Pinterest_Import_VNext'))));
const StyledOptOutOfMinorsPage = withLayoutNavRefresh(withScope(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledOptOutOfMinorsPage, c => withFooter(withContexts(withServerContext(c)))), 'optOutOfMinors_VNext'))), layoutConfigs.fullScreenWithSummaryBar);
const StyledAccountOptimizationOverviewPage = withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledQueryInsightsOverviewPage, c => withFooter(withContexts(withServerContext(withProps(c, { insightType: 'AccountOptimization' }))))), 'Route_QueryInsights_Overview_VNext')), layoutConfigs.withoutDate);
const StyledAccountOptimizationDetailsPage = withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledQueryInsightsDetailsPage, c => withFooter(withContexts(withServerContext(withProps(c, { insightType: 'AccountOptimization' }))))), 'Route_QueryInsights_Details_VNext')));
const StyledNewOpportunitiesOverviewPage = withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledQueryInsightsOverviewPage, c => withFooter(withContexts(withServerContext(withProps(c, { insightType: 'NewOpportunities' }))))), 'Route_QueryInsights_Overview_VNext')), layoutConfigs.withoutDate);
const StyledNewOpportunitiesDetailsPage = withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledQueryInsightsDetailsPage, c => withFooter(withContexts(withServerContext(withProps(c, { insightType: 'NewOpportunities' }))))), 'Route_QueryInsights_Details_VNext')));
const StyledKeywordPlannerPage = withLegacyContext(withLayoutNavRefreshWithTab(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledKeywordPlanner, c => withFooter(withContexts(withServerContext(c)))), 'Route_Campaign_KeywordPlanner_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledAdPreviewPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAdPreview, c => withFooter(withContexts(withServerContext(c)))), 'Route_AdPreview_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledAdInsightKeywordPlannerPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAdInsightKeywordPlanner, c => withFooter(withContexts(withServerContext(c)))), 'Route_Research_KeywordPlanner_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledAdInsightAdPreviewPage = withLegacyContext(withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAdInsightAdPreview, c => withFooter(withContexts(withServerContext(c)))), 'Route_AdInsightAdPreview_VNext')), layoutConfigs.fullScreenWithSummaryBar));
const StyledAppAdsAssetGroupsPage = withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAppAdsAssetGroupsPage, c => withContexts(withServerContext(c))), 'Route_AppAds_Asset_Group_VNext')), layoutConfigs.withoutDate));
const StyledUnifiedppAdsAssetGroupsCreationPage = withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUnifiedppAdsAssetGroupsCreationPage, c => withContexts(withServerContext(c))), 'Route_Unified_AppAds_Asset_Group_VNext')), layoutConfigs.withoutDate));
const StyledAssetGroupsWithPerfPage = withLegacyContext(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledAssetGroupsPage, c => withContexts(withServerContext(c))), 'Route_Asset_Group_VNext')), layoutConfigs.dateWithoutCompare));
const StyledAssetGroupEditPage = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledAssetGroupEditPage, c => withContexts(withServerContext(c))), 'Route_AssetGroupEditPage_VNext'))))));
const StyledAssetGroupCreationPage = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledAssetGroupCreationPage, c => withContexts(withServerContext(c))), 'Route_AssetGroupCreationPage_VNext'))))));
const StyledPmaxNorthStarAssetGroupEditPage = withCcJsDataContextProvider(withLegacyContext(withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPmaxNorthStarAssetGroupEditPage, c => withContexts(withServerContext(c))), 'Route_Pmax_NS_Asset_Group_VNext')), layoutConfigs.withoutDate)));
const ListingGroupsShell = withCcJsDataContextProvider(withLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.ListingGroupsShell, c => withContexts(withServerContext(c))), 'Route_Listing_Group_VNext'))));
const StyledPmaxAssetsPage = withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPmaxAssetsPage, c => withContexts(withServerContext(c))), 'Route_Assets_Grid_VNext')));
const StyledPmaxCombinationsPage = withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledPmaxCombinationsPage, c => withContexts(withServerContext(c))), 'Route_Combinations_Grid_VNext')));
const DefaultLayoutPage = withDefaultLayout(withGlobalErrorView(withPagePerfMarker(Fallback, 'DefaultGlobalErrorView')));
const SmartCampaignDefaultLayoutPage = withoutMenuLayout(withGlobalErrorView(Fallback));

const AccountsOverviewPagePreexecuteContainer = withPermissions(withAccountsSiteMap(withAppPerfMarker(withPreexecute(StyledAccountsOverview, [
  StyledAccountsPerformancePreexecution,
]))));
const OverviewPagePreexecuteContainer = withPermissions(withCampaignSiteMap(withAppPerfMarker(withPreexecute(StyledOverviewPage, [
  StyledCampaignsPreexecution, StyledAdGroupsPreexecution, StyledPositiveKeywordsPreexecution, StyledAdsPreexecution,
]))));
const CampaignsPagePreexecuteContainer = withPermissions(withCampaignSiteMap(withAppPerfMarker(withPreexecute(StyledCampaigns, [
  StyledAdGroupsPreexecution, StyledPositiveKeywordsPreexecution, StyledAdsPreexecution,
]))));

const StyledLiftMeasurementPage = withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledLiftMeasurementPage, c => withFooter(withContexts(withServerContext(c))), 'Route_LiftMeasurement_VNext'))), {
  ...layoutConfigs.fullScreen,
  ...layoutConfigs.withoutDate,
});

const StyledLiftMeasurementCreatePage = withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledLiftMeasurementCreatePage, c => withFooter(withContexts(withServerContext(c))), 'Route_LiftMeasurementCreate_VNext'))), {
  ...layoutConfigs.fullScreen,
  ...layoutConfigs.withoutDate,
});

const StyledLiftMeasurementEditPage = withFullScreenLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledLiftMeasurementEditPage, c => withFooter(withContexts(withServerContext(c))), 'Route_LiftMeasurementEdit_VNext'))), {
  ...layoutConfigs.fullScreen,
  ...layoutConfigs.withoutDate,
});

const StyledLiftMeasurementResultsPage = withLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledLiftMeasurementResultsPage, c => withFooter(withContexts(withServerContext(c))), 'Route_LiftMeasurementResults_VNext'))), {
  ...layoutConfigs.fullScreen,
  ...layoutConfigs.withoutDate,
});

// Asset Library page
const CreativeStudioAssetLibraryPageWithMenu = withLayoutNavRefresh(
  withGlobalErrorView(withPagePerfMarker(
    lazyLoad(r.CreativeStudioAssetLibraryPage, c => withFooter(withContexts(withServerContext(c)))),
    'Route_CreativeStudioAssetLibrary_VNext'
  )),
  layoutConfigs.withMenuOnly
);

// Ads Studio page
const AdsStudioPageWithMenu = withClipchampPlayer(withLayoutNavRefreshNoMenu(
  withGlobalErrorView(withPagePerfMarker(
    lazyLoad(r.AdsStudioPage, c => withFooter(withContexts(withServerContext(c)))),
    'Route_AdsStudio_VNext'
  )),
  layoutConfigs.fullScreenWithSummaryBar
));

const CampaignBudgetToolPage = withCcJsDataContextProvider(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(
  lazyLoad(r.CampaignBudgetToolPage, c => withFooter(withContexts(withServerContext(c)))),
  'Route_CampaignBudgetToolPage_VNext',
  false
))));

const BudgetCenterPage = withCcJsDataContextProvider(withFullScreenWithSummaryBarNavRefresh(withGlobalErrorView(withPagePerfMarker(
  lazyLoad(r.BudgetCenterPage, c => withFooter(withContexts(withServerContext(c))), true),
  'Route_BudgetCenterPage_VNext',
  false
))));

const BudgetCenterPageWithMenuBar = withCcJsDataContextProvider(withSummaryBarAndTabNoDateLayoutNavRefresh(withGlobalErrorView(withPagePerfMarker(
  lazyLoad(r.BudgetCenterPage, c => withFooter(withContexts(withServerContext(c))), true),
  'Route_BudgetCenterPage_VNext',
  false
))));

const StyledUnifiedAppAdsCampaignsPage = withFullScreenLayout(withGlobalErrorView(withInstrumentation(withPagePerfMarker(lazyLoad(r.StyledUnifiedAppAdsCampaignPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_UnifieddAppCampaignPage'))));
const StyledUnifiedAppAdsAssetsPage = withDefaultLayout(withGlobalErrorView(withPagePerfMarker(lazyLoad(r.StyledUnifiedAppAdsAssetsPage, c => withBingMap(withContexts(withServerContext(c)))), 'Route_UnifieddAppAdsAssetsPage')));

appRoutesInitPerfMarker.done();

const AppContent = (props) => {
  const currentRoute = window.location.pathname.toLowerCase();
  if (currentRoute !== '/campaign/vnext' &&
    currentRoute !== '/campaign/vnext/accounts' &&
    (!props.permissions.dynamic.UnifiedProductPhase2 || currentRoute !== '/campaign/vnext/accounts/overview') &&
    firstRoute === '') {
    firstRoute = currentRoute;
  }

  // As of now, page ready would be resolved by Overview and Campaign pages only, for other routes resolve page ready.
  if (!isPageReadyResolved && firstRoute !== '') {
    if (firstRoute !== currentRoute || !isLandingPage()) {
      PageReady.pageIsReady();
      isPageReadyResolved = true;
    } else {
      _.delay(() => {
        PageReady.pageIsReady();
        isPageReadyResolved = true;
      }, pageReadyTimeout);
    }
  }

  const chooseGoogleImportRoute = () => {
    if (props.scope.values.aid) {
      return props.permissions.IsSmartImportEnabled ? StyledSmartGoogleImportShell : StyledGoogleImport;
    }
    return props.permissions.IsSmartImportEnabled && props.permissions.IsSmartImportV2Enabled ? StyledSmartGoogleImportShell : StyledMultiAccountGoogleImport;
  };

  const uetTagDashboardFeature = () => {
    if (props.scope.values.tagId) {
      if (props.permissions.dynamic.UETDashboard) {
        return props.permissions.dynamic.UETDashboardPhase2 ? StyledUetDashboard : StyledUetTagsDashboard;
      }
      return StyledUetTags;
    }
    return props.permissions.dynamic.UETDashboard ? StyledUetAllTagsDashboard : StyledUetTags;
  };

  addBadgeForNewFeatures(props.i18n.getString(_TL_('New')));

  return (
    <AppContentContainer>
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <Switch>
          {/* <Route
            path="/"
            exact
            render={ExampleFeature}
          /> */}
          <Route
            path="/campaign/vnext/campaigns"
            render={CampaignsPagePreexecuteContainer}
          />
          <Route
            path="/campaign/vnext/importschedule"
            render={StyledImportSchedule}
          />
          <Route
            path="/campaign/vnext/importcredentialid"
            render={StyledImportCredentialId}
          />
          <Route
            path="/campaign/vnext/scripts"
            render={StyledScripts}
          />
          <Route
            path="/campaign/vnext/scriptsedit"
            render={StyledScriptsEdit}
          />
          <Route
            path="/campaign/vnext/scriptscreate"
            render={StyledScriptsEdit}
          />
          <Route
            path="/campaign/vnext/scriptshistory"
            render={StyledScriptsHistory}
          />
          <Route
            path="/campaign/vnext/scriptslogs"
            render={StyledScriptsLogs}
          />
          <Route
            path="/campaign/vnext/bidstrategies"
            render={BidStrategiesShell}
          />
          <Route
            path="/campaign/vnext/bulkuploads"
            render={BulkUploadsShell}
          />
          <Route
            path="/campaign/vnext/createcampaign/hotel"
            render={StyledCampaignCreationHotel}
          />
          <Route
            path="/campaign/vnext/createcampaign/lodging"
            render={StyledCampaignCreationHotel}
          />
          <Route
            path="/campaign/vnext/createcampaign/search"
            render={StyledCampaignCreationSearch}
          />
          <Route
            path="/campaign/vnext/createcampaign/searchv2"
            render={StyledCampaignCreationSearchV2}
          />
          <Route
            path="/campaign/vnext/createcampaign/audience"
            render={StyledCampaignCreationAudience}
          />
          {props.permissions.IsVideoAdsForCampaignEnabled && <Route
            path="/campaign/vnext/createcampaign/audiencevideo"
            render={StyledCampaignCreationAudienceVideo}
          />}
          <Route
            path="/campaign/vnext/createcampaign/newaudience"
            render={StyledCampaignCreationAudienceSimplified}
          />
          <Route
            path="/campaign/vnext/createcampaign/searchshopping"
            render={props.permissions.dynamic.SearchShoppingWizard && !props.permissions.IsDomesticSimplifiedChineseEnabled ? StyledCampaignCreationSearchShoppingWizard : StyledCampaignCreationSearchShopping}
          />
          <Route
            path="/campaign/vnext/createcampaign/msstoreads"
            render={StyledCampaignCreationMSStoreAds}
          />
          <Route
            path="/campaign/vnext/createcampaign/smartshopping"
            render={StyledCampaignCreationSmartShoppingPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/audienceshopping"
            render={props.permissions.dynamic.PAAIMConsolidation ? StyledCampaignCreationAudienceShoppingWizard : StyledCampaignCreationAudienceShopping}
          />
          <Route
            path="/campaign/vnext/createcampaign/performancemax"
            render={PerfMaxCampaignCreationPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/performancemaxv2"
            render={PerfMaxNorthStarCampaignCreationPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/linkedin"
            render={LinkedInCampaignCreationPage}
          />
          <Route
            path="/campaign/vnext/ad"
            render={LinkedInAdEditorPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/awareness"
            render={props.permissions.dynamic.CTVAndDealToDisplayPlus ? StyledDisplayPlusCampaignsPage : StyledXandrVideoCampaignCreationPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/awarenessmediadeal"
            render={props.permissions.dynamic.CTVAndDealToDisplayPlus ? StyledDisplayPlusCampaignsPage : StyledXandrVideoCampaignCreationPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/displayads"
            render={StyledXandrDisplayAdsCampaignCreationPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/displayplus"
            render={StyledDisplayPlusCampaignsPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/displayplusdisplay"
            render={StyledDisplayPlusCampaignsDisplayAdsPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/appads"
            render={StyledAppAdsCampaignsPage}
          />
          <Route
            path="/campaign/vnext/createcampaign/unifiedappads"
            render={StyledUnifiedAppAdsCampaignsPage}
          />
          <Route
            path="/campaign/vnext/unifiedappads/assets"
            render={StyledUnifiedAppAdsAssetsPage}
          />
          <Route
            path="/campaign/vnext/appadsassetgroups/create"
            render={StyledAppAdsAssetGroupCreation}
          />
          <Route
            path="/campaign/vnext/unifiedappadsassetgroups/create"
            render={StyledUnifiedppAdsAssetGroupsCreationPage}
          />
          <Route
            path="/campaign/vnext/unifiedappadsassetgroups/edit"
            render={StyledUnifiedppAdsAssetGroupsCreationPage}
          />
          <Route
            path="/campaign/vnext/appadsassetgroups/edit"
            render={StyledAppAdsAssetGroupCreation}
          />
          <Route
            path="/campaign/vnext/createcampaign/mran"
            render={cmpProps => <StyledSponsoredCampaignCreation {...cmpProps} isMran />}
          />
          <Route
            path="/campaign/vnext/createcampaign/sponsoredpromotion"
            render={StyledSponsoredCampaignCreation}
          />
          <Route
            path="/campaign/vnext/createcampaign/objective"
            render={CampaignOnboardingShell}
          />
          <Route
            path="/campaign/vnext/createadgroup"
            render={AdGroupCreationWithCampaign}
          />
          <Route
            path="/campaign/vnext/adgroups"
            render={StyledAdGroups}
          />
          <Route
            path="/campaign/vnext/settings"
            render={StyledSettingsForm}
          />
          <Route
            path="/campaign/vnext/locations"
            render={StyledTargetedLocationsPage}
          />
          <Route
            path="/campaign/vnext/excluded"
            render={StyledExcludedLocationsPage}
          />
          <Route
            path="/campaign/vnext/devices"
            render={StyledDevicesPage}
          />
          <Route
            path="/campaign/vnext/changehistory"
            render={StyledChangeHistory}
          />
          <Route
            path="/campaign/vnext/smartcampaigns"
            render={SmartCampaignDefaultLayoutPage}
          />
          <Route
            path="/campaign/vnext/recommendations/history"
            exact
            render={StyledRecommendationsHistoryPage}
          />
          <Route
            path="/campaign/vnext/accounts/recommendations/history"
            exact
            render={StyledMCCRecommendationsHistoryPage}
          />
          <Route
            path="/campaign/vnext/recommendations/RSAAsset"
            exact
            render={StyledRecommendationRSAAsset}
          />
          <Route
            path="/campaign/vnext/recommendations/ImageAsset"
            exact
            render={StyledRecommendationImageAsset}
          />
          <Route
            path="/campaign/vnext/recommendations/demo"
            exact
            render={StyledRecommendationDemo}
          />
          <Route
            path="/campaign/vnext/recommendations/AutoApplySettings"
            exact
            render={StyledRecommendationAutoApplySettinsPage}
          />
          <Route
            path="/campaign/vnext/accounts/recommendations/AutoApplyBulkUpload"
            exact
            render={StyledRecommendationAutoApplyBulkUploadPage}
          />
          <Route
            path="/campaign/vnext/recommendations"
            render={props.permissions.IsBAARecommendationsAsyncApplyEnabled ? StyledRecommendationsWithHistoryButton : StyledRecommendations}
          />
          <Route
            path="/campaign/vnext/accounts/recommendations"
            render={StyledMCCRecommendations}
          />
          <Route
            path="/campaign/vnext/competitionrecommendations"
            render={StyledCompetitionRecommendation}
          />
          <Route
            path="/campaign/vnext/automatedruleoperation"
            render={StyledAutomatedRuleOperation}
          />
          <Route
            path="/campaign/vnext/automatedrulehistory"
            render={StyledAutomatedRuleHistory}
          />
          <Route
            path="/campaign/vnext/automatedruledetail"
            render={StyledAutomatedRuleDetail}
          />
          <Route
            path="/campaign/vnext/bulkeditoperation"
            render={StyledBulkEdits}
          />
          <Route
            path="/campaign/vnext/bulkeditdetail"
            render={StyledBulkEditsDetail}
          />
          <Route
            path="/campaign/vnext/mcmcampaigncontrol"
            render={MCMCampaignControl}
          />
          <Route
            path="/campaign/vnext/sharedbudgets/edit"
            render={StyledSharedBudgetEdit}
          />
          <Route
            path="/campaign/vnext/sharedbudgets"
            render={StyledSharedBudgetSummary}
          />
          <Route
            path="/campaign/vnext/portfoliobidstrategy"
            render={StyledPortfolioBidStrategy}
          />
          <Route
            path="/campaign/vnext/seasonalityadjustment"
            render={StyledSeasonalityAdjustment}
          />
          <Route
            path="/campaign/vnext/competitionauction"
            render={StyledAuctionInsightPage}
          />
          <Route
            path="/campaign/vnext/campaignsauction"
            render={StyledAuctionInsightPage}
          />
          <Route
            path="/campaign/vnext/adgroupsauction"
            render={StyledAuctionInsightPage}
          />
          <Route
            path="/campaign/vnext/keywordsauction"
            render={StyledAuctionInsightPage}
          />
          <Route
            path="/campaign/vnext/feeds"
            render={StyledFeeds}
          />
          <Route
            path="/campaign/vnext/importcenter"
            render={StyledImportCenter}
          />
          <Route
            path="/campaign/vnext/businesslocationfeed"
            render={StyledBusinessLocationFeed}
          />
          <Route
            path="/campaign/vnext/productfeed"
            render={StyledProductFeeds}
          />
          <Route
            path="/campaign/vnext/linkedstore"
            render={StyledLinkedStorePage}
          />
          <Route
            path="/campaign/vnext/brand"
            render={StyledBrandPage}
          />
          <Route
            path="/campaign/vnext/feedsummary"
            render={StyledFeedsSummaryPage}
          />
          <Route
            path="/campaign/vnext/msstoreads"
            render={StyledMSStoreAdsSellerIdsPage}
          />
          <Route
            path="/campaign/vnext/keywords"
            render={StyledPositiveKeywords}
          />
          <Route
            path="/campaign/vnext/negative-keywords"
            render={StyledNegativeKeywords}
          />
          <Route
            path="/campaign/vnext/searchterms"
            render={StyledSearchTerms}
          />
          <Route
            path="/campaign/vnext/searchterms/select"
            render={StyledSearchTerms}
          />
          <Route
            path="/campaign/vnext/ads"
            render={StyledAds}
          />
          <Route
            path="/campaign/vnext/assets"
            render={StyledAssets}
          />
          <Route
            path="/campaign/vnext/assets-report"
            render={StyledAssetsReport}
          />
          <Route
            path="/campaign/vnext/combinations"
            render={StyledCombinations}
          />
          <Route
            path="/campaign/vnext/labels"
            render={StyledLabels}
          />
          <Route
            path="/campaign/vnext/labelscampaign"
            exact
            render={styledDimensionsCampaignShellWithSubTab}
          />
          <Route
            path="/campaign/vnext/labelsadgroup"
            exact
            render={styledDimensionsCampaignShellWithSubTab}
          />
          <Route
            path="/campaign/vnext/labelskeyword"
            exact
            render={styledDimensionsCampaignShellWithSubTab}
          />
          <Route
            path="/campaign/vnext/labelsad"
            exact
            render={styledDimensionsCampaignShellWithSubTab}
          />
          <Route
            path="/campaign/vnext/labelsaccount"
            exact
            render={styledDimensionsCampaignShellWithSubTab}
          />
          <Route
            path="/campaign/vnext/products"
            render={StyledProductsPage}
          />
          <Route
            path="/campaign/vnext/productgroups"
            render={ProductGroupsShell}
          />
          <Route
            path="/campaign/vnext/hotels"
            render={StyledHotelsPage}
          />
          <Route
            path="/campaign/vnext/hotelgroups"
            render={HotelGroupsShell}
          />
          <Route
            path="/campaign/vnext/properties"
            render={StyledPropertiesPage}
          />
          <Route
            path="/campaign/vnext/propertygroups"
            render={PropertyGroupsShell}
          />
          <Route
            path="/campaign/vnext/performancetargets"
            render={PerformanceTargetsShell}
          />
          <Route
            path="/campaign/vnext/adscheduleday"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adschedulehour"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adscheduledate"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adscheduleweek"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adschedulemonth"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adschedulequarter"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adscheduleyear"
            exact
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/adschedule"
            exact
            render={StyledAdSchedulePage}
          />
          <Route
            path="/campaign/vnext/adschedule/"
            strict
            render={StyledAdSchedulePage}
          />
          <Route
            path="/campaign/vnext/extensions"
            render={StyledExtensions}
          />
          <Route
            path="/campaign/vnext/audiences"
            render={StyledAudienceAssociationPage}
          />
          <Route
            path="/campaign/vnext/createassociation"
            render={StyledAudienceAssociationCreation}
          />
          <Route
            path="/campaign/vnext/overview"
            render={OverviewPagePreexecuteContainer}
          />
          <Route
            path="/campaign/vnext/audienceplanner"
            render={StyledAudiencePlanner}
          />
          <Route
            path="/campaign/vnext/audienceslibrary"
            render={props.permissions.dynamic.PerformanceMaxAudienceSignalManagementPage ? StyledAudiencesLibraryV2 : StyledAudiencesLibrary}
          />
          <Route
            path="/campaign/vnext/audiencesignals"
            render={StyledAudienceSignalManagement}
          />
          <Route
            path="/campaign/vnext/audiencecustomsegments"
            render={StyledAudienceCustomSegments}
          />
          <Route
            path="/campaign/vnext/audience/create"
            render={StyledAudienceCreation}
          />
          <Route
            path="/campaign/vnext/audience/edit"
            render={StyledAudienceEdit}
          />
          <Route
            path="/campaign/vnext/negativekeywordslists"
            render={NegativeKeywordsLists}
          />
          <Route
            path="/campaign/vnext/negativekeywordlistslibrary"
            render={NegativeKeywordListsLibrary}
          />
          <Route
            exact
            path="/campaign/vnext/accounts"
            render={AccountsPageNavigation}
          />
          <Route
            path="/campaign/vnext/accounts/overview"
            render={AccountsOverviewPagePreexecuteContainer}
          />
          <Route
            path="/campaign/vnext/accounts/performance"
            render={StyledAccountsPerformance}
          />
          <Route
            path="/campaign/vnext/accounts/management"
            render={StyledAccountsManagement}
          />
          <Route
            path="/campaign/vnext/accounts/requests"
            render={StyledAccountsRequests}
          />
          <Route
            path="/campaign/vnext/accounts/notifications"
            render={StyledAccountsNotifications}
          />
          <Route
            path="/campaign/vnext/accounts/import"
            render={StyledAccountLevelImportTab}
          />
          <Route
            path="/campaign/vnext/accounts/auctioninsights"
            render={StyledAccountsAuctionInsight}
          />
          <Route
            path="/campaign/vnext/accounts/create"
            render={StyledAccountsCreate}
          />
          <Route
            path="/campaign/vnext/accounts/bulktransferaccounts"
            render={StyledBulkTransferAccounts}
          />
          <Route
            path="/campaign/vnext/accounts/pendingtransferrequest"
            render={StyledPendingTransferRequest}
          />
          <Route
            path="/campaign/vnext/accounts/sendlinkrequest"
            render={StyledSendLinkRequest}
          />
          <Route
            path="/campaign/vnext/accounts/resendlinkrequest"
            render={StyledResendLinkRequest}
          />
          <Route
            path="/campaign/vnext/accounts/bulkunlinkaccounts"
            render={StyledBulkUnlinkAccounts}
          />
          <Route
            path="/campaign/vnext/demographics"
            render={StyledDemographicsPage}
          />
          <Route
            path="/campaign/vnext/dynamicadtargets/targets"
            exact
            render={StyledDynamicAdTarget}
          />
          <Route
            path="/campaign/vnext/dynamicadtargets/negative"
            render={DynamicExclusionShell}
          />
          <Route
            path="/campaign/vnext/dynamicadtargets/searchterms"
            render={StyledDynamicSearchTerm}
          />
          <Route
            path="/campaign/vnext/reports/history"
            render={ReportingFullScreen}
          />
          <Route
            path="/campaign/vnext/reports/v2/pollreport"
            render={ReportingFullScreen}
          />
          <Route
            path="/campaign/vnext/reports"
            render={ReportingWithNav}
          />
          <Route
            path="/campaign/vnext/reports-v4"
            render={ReportsV4WithNav}
          />
          <Route
            path="/campaign/vnext/reporting"
            render={StyledCustomReportingPage}
          />
          <Route
            path="/campaign/vnext/dimensions"
            render={StyledDimensionsShell}
          />
          <Route
            path="/campaign/vnext/experiments"
            render={StyledExperiments}
          />
          <Route
            path="/campaign/vnext/landingpages"
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/userlocationreport"
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/geographicreport"
            render={StyledDimensionsCampaignShell}
          />
          <Route
            path="/campaign/vnext/siteexclusionlists/create"
            exact
            render={WebsiteExclusionListsCreateShell}
          />
          <Route
            path="/campaign/vnext/siteexclusionlists/edit"
            exact
            render={WebsiteExclusionListsEditShell}
          />
          <Route
            path="/campaign/vnext/siteexclusionlists/viewlists"
            exact
            render={WebsiteExclusionListsViewListsShell}
          />
          <Route
            path="/campaign/vnext/siteexclusionlists/viewexclusions"
            exact
            render={WebsiteExclusionListsViewExclusionsShell}
          />
          <Route
            path="/campaign/vnext/contentexclusion/brandsafety"
            exact
            render={StyledBrandSafetyPage}
          />
          <Route
            path="/campaign/vnext/brandlists/details"
            render={StyledBrandListsDetailsPage}
          />
          <Route
            path="/campaign/vnext/brandlists"
            render={StyledBrandListsPage}
          />
          <Route
            path="/campaign/vnext/facebookimport"
            render={StyledSmartFacebookImportShell}
          />
          <Route
            path="/campaign/vnext/googleimport"
            render={chooseGoogleImportRoute()}
          />
          <Route
            path="/campaign/vnext/pinterestimport"
            render={StyledPinterestImport}
          />
          <Route
            path="/campaign/vnext/amazonimport"
            render={StyledAmazonImport}
          />
          <Route
            path="/campaign/vnext/fileimport"
            render={StyledFileImport}
          />
          <Route
            path="/campaign/vnext/conversiongoals/create"
            render={StyledConversionGoalsCreate}
          />
          <Route
            path="/campaign/vnext/conversiongoals/autocreate"
            render={StyledConversionGoalsAutoCreate}
          />
          <Route
            path="/campaign/vnext/conversiongoals/edit"
            render={StyledConversionGoalsEdit}
          />
          <Route
            path="/campaign/vnext/conversiongoals"
            render={StyledConversionGoals}
          />
          <Route
            path="/campaign/vnext/conversionvaluerules/create"
            render={StyledConversionValueRulesCreate}
          />
          <Route
            path="/campaign/vnext/conversionvaluerules/edit"
            render={StyledConversionValueRulesEdit}
          />
          <Route
            path="/campaign/vnext/conversionvaluerules"
            render={StyledConversionValueRules}
          />
          <Route
            path="/campaign/vnext/hotelcenter"
            render={StyledHotelCenter}
          />
          <Route
            path="/campaign/vnext/propertycenter"
            render={StyledHotelCenterV2}
          />
          <Route
            path="/campaign/vnext/offlineconversions"
            render={StyledOfflineConversions}
          />
          <Route
            path="/campaign/vnext/offlineconversionsschedule"
            render={StyledOfflineConversions}
          />
          <Route
            path="/campaign/vnext/conversionupliftfromconsentmode"
            render={StyledModeledConversions}
          />
          <Route
            path="/campaign/vnext/uettag"
            render={uetTagDashboardFeature()}
          />
          <Route
            path="/campaign/vnext/uettaginsights"
            render={StyledUetWebInsights}
          />
          <Route
            path="/campaign/vnext/uettagtest"
            render={StyledUetTestPage}
          />
          <Route
            path="/campaign/vnext/uettags/create"
            render={StyledUetTagsCreate}
          />
          <Route
            path="/campaign/vnext/uettags/edit"
            render={StyledUetTagsEdit}
          />
          <Route
            path="/campaign/vnext/upuettag/create"
            render={UniversalPixelUetTagCreate}
          />
          <Route
            path="/campaign/vnext/upuettag/edit"
            render={UniversalPixelUetTagEdit}
          />
          <Route
            path="/campaign/vnext/customeracquisition"
            render={StyledCustomerAcquisition}
          />
          <Route
            path="/campaign/vnext/keywordplanner"
            render={StyledKeywordPlannerPage}
          />
          <Route
            path="/campaign/vnext/adpreview"
            render={StyledAdPreviewPage}
          />
          <Route
            path="/research/vnext/keywordplanner"
            render={StyledAdInsightKeywordPlannerPage}
          />
          <Route
            path="/research/vnext/adpreview"
            render={StyledAdInsightAdPreviewPage}
          />
          <Route
            path="/campaign/vnext/merchantcenter"
            render={MerchantCenterShell}
          />
          <Route
            path="/campaign/vnext/accountsettings"
            render={StyledAccountSettingsPage}
          />
          <Route
            path="/campaign/vnext/negativekeywordslistsdetails"
            render={StyledNegativeKeywordsListsDetailsShell}
          />
          <Route
            path="/campaign/vnext/optoutforminors"
            render={StyledOptOutOfMinorsPage}
          />
          <Route
            path="/campaign/vnext/accountoptimization/details"
            render={StyledAccountOptimizationDetailsPage}
          />
          <Route
            path="/campaign/vnext/accountoptimization"
            render={StyledAccountOptimizationOverviewPage}
          />
          <Route
            path="/campaign/vnext/newopportunities/details"
            render={StyledNewOpportunitiesDetailsPage}
          />
          <Route
            path="/campaign/vnext/newopportunities"
            render={StyledNewOpportunitiesOverviewPage}
          />
          <Route
            path="/campaign/vnext/assetlibrary"
            render={CreativeStudioAssetLibraryPageWithMenu}
          />
          <Route
            path="/campaign/vnext/adstudio"
            render={AdsStudioPageWithMenu}
          />
          <Route
            path="/campaign/vnext/assetgroups/assets"
            render={StyledPmaxAssetsPage}
          />
          <Route
            path="/campaign/vnext/appadsassetgroups"
            render={StyledAppAdsAssetGroupsPage}
          />
          <Route
            path="/campaign/vnext/assetgroups/combinations"
            render={StyledPmaxCombinationsPage}
          />
          <Route
            path="/campaign/vnext/assetgroups/edit"
            render={props.permissions.dynamic.PmaxNorthStarAssetGroupSetting ? StyledPmaxNorthStarAssetGroupEditPage : StyledAssetGroupEditPage}
          />
          <Route
            path="/campaign/vnext/assetgroups/create"
            render={props.permissions.dynamic.PmaxNorthStarAssetGroupSetting ? StyledPmaxNorthStarAssetGroupEditPage : StyledAssetGroupCreationPage}
          />
          <Route
            path="/campaign/vnext/assetgroups"
            render={StyledAssetGroupsWithPerfPage}
          />
          <Route
            path="/campaign/vnext/listinggroups"
            render={ListingGroupsShell}
          />
          <Route
            path="/campaign/vnext/campaignbudgettool"
            render={CampaignBudgetToolPage}
          />
          <Route
            path="/campaign/vnext/budgetcenter"
            render={props.permissions.dynamic.NavigationRefresh && props.permissions.dynamic.NavigationRefreshBudgetCenter ? BudgetCenterPageWithMenuBar : BudgetCenterPage}
          />
          <Route
            path="/campaign/vnext/liftmeasurement/create"
            render={StyledLiftMeasurementCreatePage}
          />
          <Route
            path="/campaign/vnext/liftmeasurement/edit"
            render={StyledLiftMeasurementEditPage}
          />
          <Route
            path="/campaign/vnext/liftmeasurement/results"
            render={StyledLiftMeasurementResultsPage}
          />
          <Route
            path="/campaign/vnext/liftmeasurement"
            render={StyledLiftMeasurementPage}
          />
          <Route
            path="/campaign/vnext/:page"
            render={DefaultLayoutPage}
          />
        </Switch>
      </React.Suspense>
    </AppContentContainer>
  );
};

AppContent.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string,
    flexContainer: PropTypes.string,
  }),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.number,
      campaignId: PropTypes.number,
      tagId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    IsBAARecommendationsAsyncApplyEnabled: PropTypes.bool,
    IsSmartImportEnabled: PropTypes.bool,
    IsSmartImportV2Enabled: PropTypes.bool,
    IsVideoAdsForCampaignEnabled: PropTypes.bool,
    dynamic: PropTypes.shape({
      UnifiedProductPhase2: PropTypes.bool,
      UETDashboard: PropTypes.bool,
      NavigationRefresh: PropTypes.bool,
      NavigationRefreshBudgetCenter: PropTypes.bool,
      PerformanceMaxAudienceSignalManagementPage: PropTypes.bool,
      UETDashboardPhase2: PropTypes.bool,
      AdsCopilotChatPhase1: PropTypes.bool,
      PAAIMConsolidation: PropTypes.bool,
      CTVAndDealToDisplayPlus: PropTypes.bool,
      SearchShoppingWizard: PropTypes.bool,
      PmaxNorthStarAssetGroupSetting: PropTypes.bool,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

AppContent.defaultProps = {
  classes: {},
};

export default withScope(withI18nConsumer(withPermissions(React.memo(AppContent))));
